import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [bootstrapAvailable, setBootstrapAvailable] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadAdminProfile = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/me");
      setAdmin(data.admin || null);
      setBootstrapAvailable(!!data.bootstrap_available);
    } catch (e) {
      setAdmin(null);
      setBootstrapAvailable(false);
    }
  }, []);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("regalis_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadAdminProfile();
    } catch (e) {
      localStorage.removeItem("regalis_token");
      setUser(null);
      setAdmin(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- api is module singleton; setters are stable
  }, [loadAdminProfile]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("regalis_token", data.token);
    setUser(data.user);
    await loadAdminProfile();
    return data.user;
  };

  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("regalis_token", data.token);
    setUser(data.user);
    await loadAdminProfile();
    return data.user;
  };

  const setSession = async (token, userObj) => {
    localStorage.setItem("regalis_token", token);
    setUser(userObj);
    await loadAdminProfile();
    return userObj;
  };

  const logout = () => {
    localStorage.removeItem("regalis_token");
    setUser(null);
    setAdmin(null);
    setBootstrapAvailable(false);
  };

  const hasPermission = (perm) => {
    if (!admin) return false;
    if (admin.role === "owner_ceo") return true;
    return (admin.permissions || []).includes(perm);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        admin,
        bootstrapAvailable,
        loading,
        login,
        register,
        setSession,
        logout,
        refresh: loadMe,
        refreshAdmin: loadAdminProfile,
        hasPermission,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
